export interface UserDataFieldErrors {
    company?: string;
    firstName?: string;
    lastName?: string;
    street?: string;
    postalCode?: string;
    city?: string;
    email?: string;
    phoneNumber?: string;
    memberCount?: string;
}

export interface UserData {
    company: string;
    firstName: string;
    lastName: string;
    street: string;
    postalCode: string;
    city: string;
    email: string;
    phoneNumber: string;
    memberCount: string;
    additionalInformation: string;
}

/**
 * List of required fields for user data validation
 */
export const REQUIRED_USER_DATA_FIELDS = [
    'company',
    'firstName',
    'lastName',
    'street',
    'postalCode',
    'city',
    'email',
    'phoneNumber',
    'memberCount'
] as const;

type RequiredUserDataField = (typeof REQUIRED_USER_DATA_FIELDS)[number];

const isRequiredUserDataField = (field: keyof UserData): field is RequiredUserDataField => {
    return REQUIRED_USER_DATA_FIELDS.some((requiredField) => requiredField === field);
};

/**
 * Validates an email address format
 * @param email - The email address to validate
 * @returns true if the email is valid, false otherwise
 */
export const validateEmail = (email: string): boolean => {
    if (!email) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

/**
 * Validates a phone number format
 * @param phoneNumber - The phone number to validate
 * @returns true if the phone number is valid, false otherwise
 */
export const validatePhoneNumber = (phoneNumber: string): boolean => {
    if (!phoneNumber) return false;

    const normalized = phoneNumber.replace(/[^\d+]/g, '');

    const phoneRegex = /^\+?\d{7,15}$/;

    return phoneRegex.test(normalized);
};

/**
 * Validates a postal code format (PLZ - 5 digits)
 * @param postalCode - The postal code to validate
 * @returns true if the postal code is valid, false otherwise
 */
export const validatePostalCode = (postalCode: string): boolean => {
    if (!postalCode) return false;
    // Must be exactly 5 digits
    const postalCodeRegex = /^\d{5}$/;
    return postalCodeRegex.test(postalCode.trim());
};

/**
 * Validates a single field value
 * @param field - The field name to validate
 * @param value - The value to validate
 * @returns Error message string if validation fails, undefined if valid
 */
export const validateUserDataField = (
    field: keyof UserData,
    value: string
): string | undefined => {
    if (isRequiredUserDataField(field) && !value.trim()) {
        return 'Dieses Feld ist erforderlich';
    }

    if (field === 'email' && value && !validateEmail(value)) {
        return 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
    }

    if (field === 'phoneNumber' && value && !validatePhoneNumber(value)) {
        return 'Bitte geben Sie eine gültige Telefonnummer ein';
    }

    if (field === 'postalCode' && value && !validatePostalCode(value)) {
        return 'Bitte geben Sie eine gültige Postleitzahl ein (5 Ziffern)';
    }

    return undefined;
};
/**
 * Validates all fields and returns all errors
 * @param userData - The user data object to validate
 * @returns Object containing all validation errors
 */
export const validateAllUserDataFields = (userData: UserData): UserDataFieldErrors => {
    const errors: UserDataFieldErrors = {};
    
    REQUIRED_USER_DATA_FIELDS.forEach(field => {
        const value = userData[field];
        const error = validateUserDataField(field, value);
        if (error) {
            errors[field] = error;
        }
    });
    
    return errors;
};

